// we need to fetch more data than is used as part of this polling

import { type useProfileMagicPollingQuery } from '#app/__generated__/relay/useProfileMagicPollingQuery.graphql.js';
import { usePolling } from '#app/hooks/usePolling.js';
import { graphql } from '@contra/relay';
import { useRef, useState } from 'react';

export const useProfileMagicPolling = ({
  visitorCanEdit,
}: {
  visitorCanEdit: boolean;
}) => {
  const timer = useRef<null | number>(null);
  const [visitor, setVisitor] =
    useState<useProfileMagicPollingQuery['response']['visitor']>(undefined);

  const [isPolling, setIsPolling] = useState(false);
  const setIsPollingWithTimeout = (
    newIsPollingValue: boolean,
    onTimedOut: (() => void) | null,
  ) => {
    setIsPolling(newIsPollingValue);

    // if we are enabling polling, set a timer to disable it after 30 seconds
    if (newIsPollingValue === true) {
      timer.current = window.setTimeout(() => {
        setIsPolling(false);
        onTimedOut?.();
      }, 30_000);
    }
    // if we are disabling polling and a timer exists, clear it
    else if (newIsPollingValue === false && timer.current !== null) {
      clearTimeout(timer.current);
    }
  };

  usePolling<useProfileMagicPollingQuery>({
    disable: !visitorCanEdit || !isPolling,
    onData: (data) => {
      setVisitor(data?.visitor);
    },
    pollingInterval: '2 seconds',
    pollingIntervalWhenOutOfFocus: '2 seconds',
    query: graphql`
      query useProfileMagicPollingQuery($skip: Boolean!) {
        visitor @skip(if: $skip) {
          userAccount {
            contraMagicQuestionnaireResponse {
              whatMakesYouUnique
              whatServicesDoYouOffer
            }
            contraMagicBioSuggestions {
              id
              isSelected
              suggestion
              requestId
            }
            contraMagicOneLinerSuggestions {
              id
              isSelected
              suggestion
              requestId
            }
            profile {
              bio
              title
              portfolioProjects(type: ALL) {
                edges {
                  node {
                    id
                    llmSuggestionId
                    author {
                      id
                    }
                    ...ProjectCardV2DataWrapper_portfolioProject
                  }
                }
              }

              productizedServices {
                edges {
                  node {
                    id
                    llmSuggestionId
                    ...ProductizedServiceCardDataWrapper
                  }
                }
              }
            }
            userInterfaceFlags
            ...OpenProfileCompletionChecklistButton_userAccount
          }
        }
      }
    `,
    variables: {
      skip: !visitorCanEdit,
    },
  });

  return { setIsPollingWithTimeout, visitor };
};
